<script lang="ts">
  import type { Page, Event, Announcement, Graphic, Schedule } from "../types";
  import { EventType } from "../types";
  import { getNow, getTimeZone } from "../data/clock";
  import { eventPath } from "../support/links";
  import Countdown from "./countdown.svelte";
  import Icon from "./icon.svelte";
  import Timestamp from "./timestamp.svelte";
  import Masthead from "./masthead.svelte";
  import { srcAt } from "../support/graphics";
  import { futureEventTimes, getDailyEventTimes, getParkHours } from "../domain/events";

  export let time = getNow();
  export let timeZone = getTimeZone();
  export let page: Page;
  export let hero: Graphic;
  export let schedule: Schedule | null;
  export let events: Event[];
  export let announcements: Announcement[];

  const maxEvents = 3;

  $: eventTimes = getDailyEventTimes(events, schedule?.eventHours || [], $time, $timeZone);
  $: eventsInFuture = futureEventTimes(eventTimes, $time, EventType.Event).slice(0, maxEvents);
  $: parkHours = getParkHours(eventTimes);
</script>

<main style="--theme-color: {page.backgroundColor}">
  {#if hero}
    <Masthead src={hero} alt="" />
  {/if}

  {#if parkHours}
    <span class="ParkHours">
      Today’s Hours
      <span class="ParkHoursTimes">
        <Timestamp time={parkHours.from} />
        -
        <Timestamp time={parkHours.until} />
      </span>
    </span>
  {/if}

  <div class="InnerContainer">
    <div class="Tiles hide-scroll">
      {#each page.pageLinks as link}
        <a
          class="Tile"
          class:with-thumbnail={link.thumbnail}
          href={link.url}
          style="--text-color: {link.textColor}; --theme-color: {link.backgroundColor};"
        >
          {#if link.thumbnail}
            <div
              class="TileThumbnail"
              style="--background-image: url({srcAt(link.thumbnail, 400)})"
            ></div>
          {/if}

          {#if link.name}
            <footer class="TileFooter" class:sr-only={!link.showLabel}>
              {link.name}
            </footer>
          {/if}
        </a>
      {/each}
    </div>

    {#if eventsInFuture.length > 0}
      <div class="Events">
        <h2 class="SectionHeading">Upcoming Events</h2>

        {#each eventsInFuture as entry}
          <a href={eventPath(entry)} class="Event">
            <img
              src={entry.graphic ? srcAt(entry.graphic, 192) : hero}
              class="EventGraphic"
              class:placeholder={!entry.graphic}
              alt=""
            />
            <span class="EventTitle">{entry.title}</span>
            <Countdown deadline={entry.from} {time} {timeZone} />
          </a>
        {/each}
      </div>
    {/if}

    {#if announcements.length > 0}
      <div class="Announcements">
        <h2 class="SectionHeading">Announcements</h2>
        <ul class="AnnouncementList">
          {#each announcements as announcement}
            <li class="Announcement">
              {#if announcement.iconSrc}
                <span class="AnnouncementIconSlot">
                  <Icon icon={{ src: announcement.iconSrc }} fill={announcement.iconColor} />
                </span>
              {/if}
              <div class="AnnouncementMessage">
                {@html announcement.message}
              </div>
            </li>
          {:else}
            <li>No announcements for today.</li>
          {/each}
        </ul>
      </div>
    {/if}
  </div>
</main>

<style>
  main {
    --border-radius: 6px;
    --outer-padding: calc(var(--sp) * 16);
    --grid-gap: calc(var(--sp) * 8);
    --box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    --section-spacing: calc(var(--sp) * 32);

    background: var(--theme-bg-elevation-0);
    flex: 1 1 auto;
    hyphens: auto;
    overflow: auto;
    padding-bottom: max(env(safe-area-inset-bottom), var(--outer-padding));
    width: 100%;
  }

  .ParkHours {
    background: color-mix(in lab, var(--theme-color, black), white 10%);
    color: white;
    display: flex;
    font-size: 14px;
    font-weight: 600;
    grid-gap: calc(var(--sp) * 16);
    justify-content: center;
    line-height: 1.16em;
    padding: 8px 16px;
    text-align: center;
    width: 100%;
  }

  .ParkHoursTimes {
    font-size: 18px;
    font-weight: 400;
  }

  .InnerContainer {
    flex: 1 1 auto;
    padding-top: 16px;
    margin: 0 auto;
    width: 100%;
  }

  .Tiles {
    display: flex;
    overflow-y: hidden;
    overflow-x: auto;
    grid-gap: var(--grid-gap);
    padding-left: max(env(safe-area-inset-left), var(--outer-padding));
    padding-right: max(env(safe-area-inset-right), var(--outer-padding));
    padding-bottom: var(--outer-padding); /* need space for shadow */
    margin-bottom: calc(var(--outer-padding) * -1);
  }

  .Tile {
    --size: 170px;
    background-color: var(--theme-color, var(--theme-elevation-1));
    border-radius: var(--border-radius);
    box-shadow: var(--box-shadow);
    color: var(--text-color, #fff);
    display: flex;
    flex: 0 0 var(--size);
    font-size: 14px;
    line-height: 1.33;
    font-weight: 500;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
    text-decoration: none;
    text-align: center;
    width: var(--size);
    height: var(--size);
  }

  .TileThumbnail {
    background-image: var(--background-image);
    background-size: cover;
    background-position: 50% 50%;
    display: block;
    flex: 1 1;
    width: 100%;
  }

  .TileFooter {
    align-items: center;
    background-color: var(--theme-color, var(--theme-elevation-1));
    display: flex;
    flex: 0 0;
    justify-content: center;
    padding: 12px 8px;
    width: 100%;
  }

  .SectionHeading {
    flex: 1 1;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.12em;
    line-height: 1;
    margin: 0;
    padding: 16px 0;
    position: relative;
    text-transform: uppercase;
    top: 1px; /* align with apex of arrow */
  }

  .Events {
    display: flex;
    flex-direction: column;
    margin-top: var(--section-spacing);
    padding-left: max(env(safe-area-inset-left), var(--outer-padding));
    padding-right: max(env(safe-area-inset-right), var(--outer-padding));
  }

  .Event {
    --graphic-width: 86px;
    --inline-padding: var(--outer-padding);

    background: var(--theme-bg-elevation-1);
    box-shadow: var(--box-shadow);
    border-radius: var(--border-radius);
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-decoration: none;
    text-wrap: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    overflow: hidden;
    min-height: 84px;
    position: relative;
    padding-left: calc(var(--graphic-width) + var(--inline-padding));
    padding-right: var(--inline-padding);
    margin-bottom: 8px;
    width: 100%;
    font-size: 14px;
    line-height: 16px;
  }

  .Event:last-child {
    margin-bottom: 0;
  }

  .EventGraphic {
    background-color: var(--theme-color);
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: var(--graphic-width);
  }

  .EventGraphic.placeholder {
    padding: var(--grid-gap);
  }

  .EventTitle {
    display: block;
    font-size: 16px;
    line-height: 26px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .Announcements {
    margin-top: var(--section-spacing);
    padding-left: max(env(safe-area-inset-left), var(--outer-padding));
    padding-right: max(env(safe-area-inset-right), var(--outer-padding));
  }

  .AnnouncementList {
    border-width: 1px 0;
    display: flex;
    flex-direction: column;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .Announcement {
    border-top: 1px solid var(--color-tooling);
    align-items: flex-start;
    display: flex;
    justify-content: flex-start;
    margin: 0;
    padding: calc(var(--sp) * 12) 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px; /* align to icon */
  }

  .AnnouncementMessage :global(*:first-child) {
    margin-top: 0;
  }

  .AnnouncementMessage :global(*:last-child) {
    margin-bottom: 0;
  }

  .AnnouncementIconSlot {
    flex: 0 0 24px;
    margin-right: 12px;
  }
</style>
